<script lang="ts">
import { defineComponent } from 'vue'
export default defineComponent({
  name: 'navbar',
})
</script>
<template>
  <section
    :class="[
      'maia-custom-navbar',
      submenuPermissionRoutes.length ? 'has-submenu' : ''
    ]"
  >
    <section class="navbar-left">
      <h1
        :class="collapseShow?'':'width0'"
        class="sys-title"
      >
        <span class="title">{{ tenantTitle || '徐房物业' }}</span>
        <span class="split"></span>
        <span class="sub-title">智慧空间</span>
      </h1>
      <template v-if="!projectHide">
        <base-project-multiple-select
          v-if="projectMultipleSelect"
          :disabled="projectDisabled"
          class="mr-12"
        />
        <base-project-select
          v-else
          :disabled="projectDisabled"
          class="mr-12"
        />
      </template>
      <breadcrumb :nb-breadcrumbs="nbBreadcrumbs" />
    </section>
    <section class="navbar-right">
      <template v-if="showTenantSelect">
        <base-tenant-select />
        <el-divider direction="vertical" />
      </template>
      <div
        v-if="VITE_APP_MODEL_SHOW === '1'"
        class="change-model"
        @click="handleModelSwitchClick"
      >
        <base-svg-icon
          v-if="appStore.modelSwitch === 0"
          icon-class="icon-model-on"
          class-name="icon-model"
        />
        <base-svg-icon
          v-else
          icon-class="icon-model-off"
          class-name="icon-model"
        />
      </div>
      <section
        v-permission:FM="['AGENT']"
        class="icon-ai-assistant"
        @click="handleAiAssistantClick"
      >
        <base-svg-icon
          icon-class="icon-ai"
          class-name="icon-ai"
        />
        <span class="ai-label">AI小智</span>
      </section>
      <el-divider
        v-permission:FM="['AGENT']"
        direction="vertical"
      />
      <template v-if="showAgiChat">
        <img
          class="icon-agichat"
          src="@/assets/images/icon-agichat.png"
          @click="handleAgichatClick"
        />
        <el-divider direction="vertical" />
      </template>
      <span
        class="navbar-right__icon download"
        @click="handleShowDownload"
      >
        <base-svg-icon
          icon-class="icon-download"
        />
      </span>
      <el-divider direction="vertical" />
      <el-dropdown trigger="hover">
        <span class="dropdown-link">
          <base-svg-icon
            icon-class="username"
            class-name="icon__username"
          />
          <span class="username">{{ userInfo.realName || '--' }}</span>
        </span>
        <template #dropdown>
          <el-dropdown-menu>
            <el-dropdown-item
              @click="logoutHandle"
            >
              退出登录
            </el-dropdown-item>
          </el-dropdown-menu>
        </template>
      </el-dropdown>
      <el-divider direction="vertical" />
      <span
        id="fullScreen"
        class="navbar-right__icon no-pr"
        @click="fullscreenToggle"
      >
        <base-svg-icon
          icon-class="icon-fullscreen"
        />
      </span>
    </section>

    <base-download-center ref="baseDownloadCenterRef" />
    <ai-assistant ref="aiAssistantRef" />
  </section>
</template>
<script lang="ts" setup name="navbar">
  import {
    computed, ref, watch,
  } from 'vue';
  import { getLogoutUrl } from '@/utils/index';
  import { useUserStore } from '@/store/user';
  import { useTenantStore } from '@/store/tenant';
  import { useAppStore } from '@/store/app';
  import { useFullscreen } from '@vueuse/core';
  import BaseProjectSelect from '@/components/base-project-select/index.vue';
  import BaseDownloadCenter from '@/components/base-download-center/index.vue';
  import BaseProjectMultipleSelect from '@/components/base-project-multiple-select/index.vue';
  import BaseTenantSelect from '@/components/base-tenant-select/index.vue';
  import Breadcrumb from '../breadcrumb/index.vue';
  import AiAssistant from '../ai-assistant/index.vue';

  const props = defineProps({
    navbarBreadcrumbs: {
      type: Array<any>,
      default: [],
    },
    projectDisabled: {
      type: Boolean,
      default: false,
    },
    projectHide: {
      type: Boolean,
      default: false,
    },
    projectMultipleSelect: {
      type: Boolean,
      default: false,
    },

    // 顶部二级导航组件所需参数
    permissionRoutes: {
      type: Array<any>,
      default: [],
    },
    submenuPermissionRoutes: {
      type: Array<any>,
      default: [],
    },
    activeMenu: {
      type: String,
      default: '',
    },
    collapseShow: {
      type: Boolean,
      default: true,
    },
    agiWebChatRef: {
      type: Object,
      default: null,
    },
    // 是否显示 AgiChat 图标（根据租户配置决定）
    showAgiChat: {
      type: Boolean,
      default: false,
    },
  });

  const nbBreadcrumbs = ref();
  const appStore = useAppStore();

  const {
    VITE_APP_MODEL_SHOW,
  } = import.meta.env;

  watch(() => props.navbarBreadcrumbs, (newValue) => {
    nbBreadcrumbs.value = newValue;
  }, {
    immediate: true,
  });
  const { toggle: fullscreenToggle } = useFullscreen();

  const logoutHandle = async() => {
    const result:TypeObject = await useUserStore().logout();
    location.href = getLogoutUrl(result.loginUrl || '');
  };

  const userInfo = computed(() => useUserStore().userInfo);
  const tenantStore = useTenantStore();

  // 当租户数量大于1时才显示租户选择器
  const showTenantSelect = computed(() => tenantStore.tenantList.length > 1);

  // 获取租户配置的标题
  const tenantTitle = computed(() => {
    const currentTenant = tenantStore.currentTenant;
    if (currentTenant?.expandInfo) {
      let expandInfo = currentTenant.expandInfo;
      if (typeof expandInfo === 'string') {
        try {
          expandInfo = JSON.parse(expandInfo);
        } catch {
          // 解析失败使用默认值
        }
      }
      return (expandInfo as any)?.title || '';
    }
    return '';
  });

  const baseDownloadCenterRef = ref();
  const handleModelSwitchClick = () => {
    appStore.setModelSwitch(appStore.modelSwitch === 1 ? 0 : 1);
    appStore.setLoadingStatus(true);
    setTimeout(() => {
      appStore.setLoadingStatus(false);
    }, 1000);
  };
  const handleShowDownload = () => {
    baseDownloadCenterRef.value && baseDownloadCenterRef.value.handleOpenDialog();
  };

  const handleAgichatClick = () => {
    props.agiWebChatRef && props.agiWebChatRef.open && props.agiWebChatRef.open(1);
  };

  const aiAssistantRef = ref();

  const handleAiAssistantClick = () => {
    aiAssistantRef.value && aiAssistantRef.value.open();
  };
</script>

<style lang="scss" scoped>
:deep(.ma-input__wrapper){
  background: rgba(255, 255, 255, 0.4);
}

.sys-title {
  display: flex;
  align-content: center;
  align-items: center;
  justify-content: center;
  width: 140px;
  height: var(--maia-nav-header-height);
  overflow: hidden;
  color: #494B4C;
  white-space: nowrap;
  transition: all 0.3s linear;

  .title {
    color: #111;
    font-weight: 600;
    font-size: 20px;

    /* stylelint-disable-next-line font-family-no-missing-generic-family-keyword */
    font-family: YouSheBiaoTiHei;
    line-height: normal;
    white-space: nowrap;
  }

  .split {
    width: 2px;
    height: 2px;
    margin: 0 6px;
    background-color: #494B4C;
  }

  .sub-title {
    width: 50px;
    font-weight: 600;
    font-size: 12px;
  }

  &.width0{
    width: 0;

    .title{
      width: 0;
    }

    .sub-title {
      width: 0;
    }
  }
}

.maia-custom-navbar {
  --maia-left-or-right-width: 450px;

  @include flex-box();

  box-sizing: border-box;
  width: calc(100vw - var(--maia-aside-width));
  height: var(--maia-nav-header-height);
  padding: 0 12px 0 6px;
  background: linear-gradient(90deg, rgba(237, 246, 255, 0) 0%, rgba(237, 246, 255, 0.6) 10%),
    linear-gradient(262.34deg, rgba(211, 225, 239, 0) 7.75%, rgba(212, 225, 239, 0.2) 90.1%);
  backdrop-filter: blur(4px);

  .navbar-left {
    display: flex;
    flex: none;
    align-items: center;
    justify-content: flex-start;
    width: 800px;

    .project-wrap {
      display: flex;
      align-items: center;

      .project-item {
        position: relative;
        height: 100%;
        margin: 0 18px;
        font-size: 12px;
        line-height: 28px;
        cursor: pointer;

        &.current {
          color: #FFF;
          font-weight: 700;

          &:after {
            position: absolute;
            bottom: 0;
            left: 0;
            width: 100%;
            height: 2px;
            background: #FFF;
            border-radius: 6px;
            box-shadow: 0 1px 3px rgba(20, 116, 243, 0.3);
            content: '';
          }
        }
      }
    }
  }

  .navbar-right {
    @mixin common {
      display: flex;
      align-items: center;
      padding: 0 10px;
      cursor: pointer;
    }

    @include flex-box(flex-end);

    width: var(--maia-left-or-right-width);

    .change-model{
      :deep(.icon-model){
        font-size: 36px;
        cursor: pointer;
      }
    }

    .icon-agichat {
      width: 60px;
      height: 20px;
      padding: 0 10px;
      cursor: pointer;
    }

    .icon-ai-assistant {
      display: flex;
      gap: 4px;
      align-items: center;
      padding: 4px 10px;
      color: #606266;
      border-radius: 6px;
      cursor: pointer;
      transition: background 0.2s,color 0.2s;

      :deep(.icon-ai) {
        font-size: 18px;
      }

      .ai-label {
        font-size: 13px;
        line-height: 1;
        white-space: nowrap;
        background: linear-gradient(83.74deg, #30ACFF 1.85%, #3084FF 16.6%, #2664FF 60.35%, #BA69FF 104.1%);
        background-clip: text;
        -webkit-text-fill-color: transparent;
      }
    }

    .navbar-right__icon {
      @include common();

      font-size: 20px;

      &.no-pr {
        padding-right: 0;
      }

      &.download {
        color: #86909C;
      }

      .text-size {
        font-size: 12px;
      }
    }

    .dropdown-link {
      @include common();

      flex: none;

      .icon__username {
        margin-right: 5px;
        font-size: 20px;
      }

      .username {
        min-width: 20px;
        padding: 0 4px;
      }
    }

    @mixin common {
      display: flex;
      align-items: center;
      padding: 0 10px;
      cursor: pointer;
    }
  }
}
</style>
